import { useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { IcDeleteTrash } from '@/assets/images';
import { SelectItem } from '@/components/FormElements/Select/Select';
import { Button } from '@/components/TailwindComponents';
import {
  AttemptIndicator,
  IArrestCharge,
  IOffense,
  IPenalCode,
} from '@/models';
import { useToastStore } from '@/store';
import { colors } from '@/theme/variables';
import { getEffectiveClassAndCategory, getPenalCodeLabel } from '@/utils';

import { AttemptIndicatorSelectItems } from '../../../../data';
import { useKaseArrestsContext } from '../../../KaseArrestsContext';
import { FormFieldRenderer } from '../components';
import { offenseDetailsGroupFields } from '../data';
import { IArrestFormData } from '../types';

interface OffenseDetailsTabProps {
  viewMode?: boolean;
  penalCodes: IPenalCode[];
  offensesByPenalCode?: Map<string, IOffense>;
}

interface IOffenseDetailRow {
  id: string;
  penalCodeId?: string | null;
  attemptIndicator?: AttemptIndicator | null;
  nameOfOffense?: string | null;
  count?: number | null;
  ncicCode?: string | null;
  type?: string | null;
  lawTitle?: string | null;
  section?: string | null;
  subsection?: string | null;
  statutoryClass?: string | null;
  category?: string | null;
  degree?: string | null;
}

const OffenseDetailsTab = ({
  viewMode = false,
  penalCodes = [],
  offensesByPenalCode,
}: OffenseDetailsTabProps) => {
  const { updateToast } = useToastStore();
  const { currentArrestee, setCurrentArrestee } = useKaseArrestsContext();
  const {
    control,
    formState: { errors },
  } = useFormContext<IArrestFormData>();

  const [selectedLawId, setSelectedLawId] = useState<string | null>(null);
  const [offenseRows, setOffenseRows] = useState<IOffenseDetailRow[]>([]);
  const syncingRef = useRef(false);
  const initializedRef = useRef(false); // Track if we've initialized from arrestCharges
  const initializingRef = useRef(false); // Track if we're actively initializing
  const expectedRowCountRef = useRef<number>(0); // Track expected row count after initialization
  const lastArrestChargesRef = useRef<IArrestCharge[] | undefined | null>();
  const lastArresteeIdRef = useRef<string | undefined>();
  const restoringRef = useRef(false); // Track if we're restoring from cancel

  // Reset initialization when arrestee changes
  useEffect(() => {
    if (currentArrestee?._id !== lastArresteeIdRef.current) {
      lastArresteeIdRef.current = currentArrestee?._id;
      initializedRef.current = false;
      initializingRef.current = false;
      expectedRowCountRef.current = 0;
      lastArrestChargesRef.current = undefined;
    }
  }, [currentArrestee?._id]);

  // Initialize offense rows from currentArrestee.arrestCharges
  useEffect(() => {
    // Check if arrestCharges changed externally (e.g., form reset/cancel)
    const currentCharges = currentArrestee?.arrestCharges;
    const hasChanged = lastArrestChargesRef.current !== currentCharges;

    if (hasChanged) {
      lastArrestChargesRef.current = currentCharges;
      // Reset initialization state when arrestCharges changes
      initializedRef.current = false;
      initializingRef.current = true; // Mark that we're starting initialization
      syncingRef.current = true; // Prevent sync during re-initialization
      restoringRef.current = true; // Mark that we're restoring (e.g., from cancel)

      if (currentCharges && currentCharges.length > 0) {
        const rows: IOffenseDetailRow[] = currentCharges.map(
          (charge, index) => {
            const effective = getEffectiveClassAndCategory(
              charge?.penalCode,
              charge.attemptIndicator,
            );
            return {
              id: `charge-${index}-${charge.penalCode?._id}`,
              penalCodeId: charge?.penalCode?._id || null,
              attemptIndicator: charge.attemptIndicator || null,
              nameOfOffense: charge?.penalCode?.lawDescription || '',
              lawTitle: charge?.penalCode?.lawTitle || '',
              section: charge?.penalCode?.section || '',
              subsection: charge?.penalCode?.subsection || '',
              statutoryClass: effective.statutoryClass || '',
              category: effective.category || '',
              degree: charge?.penalCode?.degree ?? '',
              count: charge.count ?? undefined,
              ncicCode: charge?.penalCode?.ncicCode || '',
              type: charge?.type || '',
            };
          },
        );
        expectedRowCountRef.current = rows.length; // Track expected count
        setOffenseRows(rows);
      } else {
        expectedRowCountRef.current = 0;
        setOffenseRows([]);
      }
    }
  }, [currentArrestee?.arrestCharges, viewMode]);

  // Mark as initialized AFTER offenseRows state has been updated to expected count
  useEffect(() => {
    if (
      initializingRef.current &&
      !initializedRef.current &&
      offenseRows.length === expectedRowCountRef.current
    ) {
      initializedRef.current = true;
      initializingRef.current = false;
      // Allow sync after initialization completes
      syncingRef.current = false;

      // Reset restoring flag after a brief delay to allow the UI to settle
      // This ensures the sync effect doesn't immediately overwrite restored data
      setTimeout(() => {
        restoringRef.current = false;
      }, 100);
    }
  }, [offenseRows]);

  // Sync offenseRows to currentArrestee.arrestCharges whenever rows change
  useEffect(() => {
    // Only sync after initialization is complete and not currently initializing/restoring
    if (
      !currentArrestee ||
      syncingRef.current ||
      !initializedRef.current ||
      initializingRef.current ||
      restoringRef.current // Don't sync when restoring from cancel
    )
      return;

    // Convert rows to IArrestCharge format (only complete rows)
    const arrestCharges: IArrestCharge[] = offenseRows
      .filter((row) => row.penalCodeId && row.attemptIndicator)
      .map((row) => {
        const penalCode = penalCodes.find((pc) => pc?._id === row.penalCodeId);
        if (!penalCode || !row.attemptIndicator) return null;

        const charge: IArrestCharge = {
          penalCode,
          attemptIndicator: row.attemptIndicator,
          count: row.count ?? undefined,
          type: row.type ?? undefined,
        };
        return charge;
      })
      .filter((charge): charge is IArrestCharge => charge !== null);

    // Prevent sync loop
    syncingRef.current = true;
    setCurrentArrestee({
      ...currentArrestee,
      arrestCharges,
    });
    lastArrestChargesRef.current = arrestCharges;

    // Reset sync flag after state update
    setTimeout(() => {
      syncingRef.current = false;
    }, 0);
  }, [offenseRows, penalCodes]);

  const handleAddNewPenalCode = () => {
    if (!selectedLawId) {
      updateToast({
        open: true,
        message: 'Please select a penal code first.',
      });
      return;
    }

    // Check if this penal code is already added
    const isAlreadyUsed = offenseRows.some(
      (row) => row.penalCodeId === selectedLawId,
    );

    if (isAlreadyUsed) {
      updateToast({
        open: true,
        message: 'This penal code has already been added.',
      });
      return;
    }

    const selectedPenalCode = penalCodes.find(
      (pc) => pc?._id === selectedLawId,
    );
    if (!selectedPenalCode) return;

    // Look up offense details to pre-populate ONLY for new rows
    // This is a brand new offense being added by the user, so we pre-populate from Crime Offense
    const correspondingOffense = offensesByPenalCode?.get(selectedLawId);

    const effective = getEffectiveClassAndCategory(
      selectedPenalCode,
      correspondingOffense?.attemptIndicator,
    );
    const newRow: IOffenseDetailRow = {
      id: `new-${Date.now()}`,
      penalCodeId: selectedLawId,
      // Pre-populate from offense if available (ONLY for new rows)
      attemptIndicator: correspondingOffense?.attemptIndicator || null,
      nameOfOffense: selectedPenalCode.lawDescription || '',
      count: correspondingOffense?.count,
      ncicCode: selectedPenalCode.ncicCode || '',
      type: '',
      lawTitle: selectedPenalCode.lawTitle || '',
      section: selectedPenalCode.section || '',
      subsection: selectedPenalCode.subsection || '',
      statutoryClass: effective.statutoryClass || '',
      category: effective.category || '',
      degree: selectedPenalCode?.degree,
    };

    setOffenseRows([...offenseRows, newRow]);
    // Clear selection after adding
    setSelectedLawId(null);
  };

  const handleFieldChange = (rowId: string, fieldName: string, value: any) => {
    const updatedRows = offenseRows.map((row) => {
      if (row.id === rowId) {
        if (fieldName === 'count') {
          const raw = value?.target?.value ?? value;
          if (raw === '' || raw === null || raw === undefined) {
            return { ...row, count: undefined };
          }
          const parsed = parseInt(String(raw), 10);
          return { ...row, count: Number.isNaN(parsed) ? undefined : parsed };
        }

        if (fieldName === 'attemptIndicator') {
          const penalCode = penalCodes.find(
            (pc) => pc?._id === row.penalCodeId,
          );
          const effective = getEffectiveClassAndCategory(penalCode, value);
          return {
            ...row,
            attemptIndicator: value,
            statutoryClass: effective.statutoryClass || '',
            category: effective.category || '',
          };
        }

        return { ...row, [fieldName]: value };
      }
      return row;
    });

    setOffenseRows(updatedRows);
  };

  const handleRemoveOffenseRow = (rowId: string) => {
    const updatedRows = offenseRows.filter((row) => row.id !== rowId);
    setOffenseRows(updatedRows);
  };

  const getPenalCodeOptions = (): SelectItem[] => {
    // Get IDs of already selected penal codes
    const selectedPenalCodeIds = offenseRows
      .map((row) => row.penalCodeId)
      .filter((id): id is string => id !== null);

    // Filter out already selected penal codes
    return penalCodes
      .filter((pc) => !selectedPenalCodeIds.includes(pc?._id || ''))
      .map((pc) => ({
        label: getPenalCodeLabel(pc),
        value: pc?._id || '',
      }));
  };

  const getFieldItems = (itemsKey: string): SelectItem[] => {
    switch (itemsKey) {
      case 'penalCodes':
        return getPenalCodeOptions();
      case 'attemptIndicator':
        return AttemptIndicatorSelectItems as SelectItem[];
      default:
        return [];
    }
  };

  // Get field groups
  const lawFieldGroup = offenseDetailsGroupFields[0]; // GROUP 1: Law Selection
  const offenseDetailsFieldGroup = offenseDetailsGroupFields[1]; // GROUP 2: Offense Details (includes attemptIndicator)

  // Get the Law field from group 1
  const lawField = lawFieldGroup.fields.find(
    (field) => field.name === 'offense',
  );

  if (viewMode && offenseRows.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800">
        <p className="text-sm">No offense details have been added yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full">
      {/* Law Selection - Only shown in edit mode, at the top */}
      {!viewMode && lawField && (
        <div className="space-y-4">
          <div className="grid grid-cols-12 gap-4">
            <div className={lawField.className}>
              <FormFieldRenderer
                field={{
                  ...lawField,
                  name: 'selectedLaw',
                }}
                control={control}
                errors={errors}
                viewMode={false}
                getItems={getFieldItems}
                customValue={selectedLawId}
                onCustomChange={(value: any) =>
                  setSelectedLawId(value as string)
                }
              />
            </div>
          </div>

          {/* Add New Penal Code Button */}
          <div className="flex justify-start">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="w-fit text-sm"
              onClick={handleAddNewPenalCode}
            >
              <span className="text-xl">+</span>
              Add
            </Button>
          </div>
        </div>
      )}

      {/* Offense Rows */}
      {offenseRows.length > 0 && (
        <div className="space-y-6">
          {offenseRows.map((row, rowIndex) => {
            return (
              <div key={row.id} className="space-y-4">
                {/* Horizontal line separator */}
                {(rowIndex > 0 || !viewMode) && (
                  <hr className="border-gray-300 dark:border-gray-600" />
                )}

                {/* Show fields when penalCodeId is set */}
                {row.penalCodeId && (
                  <>
                    {/* Offense Details (all fields from group 2) */}
                    <div className="grid grid-cols-12 gap-4">
                      {offenseDetailsFieldGroup.fields.map((field) => {
                        const uniqueFieldName = `${field.name}-${row.id}`;
                        const fieldValue = (row as any)?.[field.name] ?? null;

                        return (
                          <div
                            key={uniqueFieldName}
                            className={field.className}
                          >
                            <FormFieldRenderer
                              field={{
                                ...field,
                                name: uniqueFieldName,
                              }}
                              control={control}
                              errors={errors}
                              viewMode={viewMode}
                              getItems={getFieldItems}
                              customValue={fieldValue}
                              onCustomChange={(value: any) =>
                                handleFieldChange(
                                  row.id,
                                  field.name,
                                  value as string,
                                )
                              }
                            />
                          </div>
                        );
                      })}
                    </div>

                    {/* Remove offense button - only in edit mode */}
                    {!viewMode && (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveOffenseRow(row.id)}
                          className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
                          title="Remove offense"
                        >
                          <IcDeleteTrash
                            color={colors.error.main}
                            className="w-5 h-5 cursor-pointer"
                          />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state for edit mode when no rows */}
      {!viewMode && offenseRows.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800">
          <p className="text-sm">
            Select an offense above and click &quot;Add&quot; to add offense
            details.
          </p>
        </div>
      )}
    </div>
  );
};

export default OffenseDetailsTab;
